import React, { useState, useEffect } from 'react';
import { userAPI } from '../../../services/api';
import { roleAPI, userRoleAPI } from '../../../services/rbacApi';
import { useAuth } from '../../../contexts/AuthContext';
import TextInput from '../../Common/TextInput';
import SearchableRoleDropdown from '../../Common/SearchableRoleDropdown';
import AdditionalRolesSelect from './AdditionalRolesSelect';

const formatRoleLabel = (role) => {
  if (!role) return '';
  return `${role.name}${role.level ? ` (Level ${role.level})` : ''}`;
};

const UserEditForm = ({ user, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    primaryRole: '',
    department: '',
    phone: '',
    isActive: true
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedRoleLabel, setSelectedRoleLabel] = useState('');
  const [additionalRoles, setAdditionalRoles] = useState([]);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        primaryRole: user.primaryRole || '',
        department: user.department || '',
        phone: user.phone || '',
        isActive: user.isActive !== false
      });
      setSelectedRoleLabel(user.primaryRole || '');
    }
  }, [user]);

  useEffect(() => {
    const userId = user?._id || user?.id;
    if (!userId) return;

    const loadRoleAssignments = async () => {
      try {
        const response = await userRoleAPI.getUserRoles(userId);
        const assignments = response.data?.data?.roles || [];
        const primary = (user.primaryRole || formData.primaryRole || '').toUpperCase();
        const extra = assignments
          .filter((a) => a.isActive !== false)
          .map((a) => a.roleCode)
          .filter((code) => code && code.toUpperCase() !== primary);
        setAdditionalRoles([...new Set(extra)]);
      } catch (err) {
        console.error('Failed to load user role assignments:', err);
      }
    };

    loadRoleAssignments();
  }, [user?._id, user?.id, user?.primaryRole]);

  useEffect(() => {
    if (!formData.primaryRole) {
      setSelectedRoleLabel('');
      return;
    }

    let isActive = true;

    const loadRoleLabel = async () => {
      try {
        const response = await roleAPI.getRole(formData.primaryRole);
        const role = response.data?.data?.role;

        if (isActive && role) {
          setSelectedRoleLabel(formatRoleLabel(role));
        }
      } catch (error) {
        console.error('Failed to load role details:', error);
        if (isActive) {
          setSelectedRoleLabel(formData.primaryRole);
        }
      }
    };

    loadRoleLabel();

    return () => {
      isActive = false;
    };
  }, [formData.primaryRole]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleChange = (roleCode, label) => {
    setFormData(prev => ({
      ...prev,
      primaryRole: roleCode
    }));
    setSelectedRoleLabel(label);
    setAdditionalRoles((prev) =>
      prev.filter((code) => code.toUpperCase() !== (roleCode || '').toUpperCase())
    );

    if (errors.primaryRole) {
      setErrors(prev => ({
        ...prev,
        primaryRole: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.primaryRole) {
      newErrors.primaryRole = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        primaryRole: formData.primaryRole,
        additionalRoles,
        department: formData.department,
        phone: formData.phone,
        isActive: formData.isActive
      };
      
      await userAPI.updateUser(user._id || user.id, userData);
      onSuccess && onSuccess();
    } catch (error) {
      console.error('Failed to update user:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Failed to update user. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const { isOnlyAdmin, isSuperAdmin } = useAuth();
  const isAdminOnly = typeof isOnlyAdmin === 'function'
    ? isOnlyAdmin()
    : (typeof isSuperAdmin === 'function' ? !isSuperAdmin() : Boolean(isOnlyAdmin));
  const isSuperAdminUser = (user?.primaryRole || '').toUpperCase() === 'SUPER_ADMIN';

  if (!user) {
    return null;
  }

  if (isAdminOnly && isSuperAdminUser) {
    return (
      <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
        <div className="relative w-full max-w-md bg-white rounded-lg shadow-2xl border border-gray-100 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Protected Account</h3>
          <p className="text-sm text-gray-600 mb-6">
            This account has the Super Administrator role and is protected. Only Super Administrators can view or modify this account.
          </p>
          <button
            onClick={onCancel}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
      <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-2xl border border-gray-100 max-h-[95vh] overflow-y-auto">
        {/* Compact Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Edit User</h3>
            </div>
            <button
              onClick={onCancel}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4">
          {errors.submit && (
            <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-800">{errors.submit}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Main Fields Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Personal Information */}
                <TextInput
                  variant="input"
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  error={errors.firstName}
                  required
                />

                <TextInput
                  variant="input"
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  error={errors.lastName}
                  required
                />

                {/* Email */}
                <TextInput
                  variant="input"
                  type="email"
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@company.com"
                  error={errors.email}
                  required
                />
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Role Selection */}
                <div>
                  <label htmlFor="primaryRole" className="block text-sm font-semibold text-gray-700 mb-2">
                    Primary role *
                  </label>
                  <SearchableRoleDropdown
                    value={formData.primaryRole}
                    onChange={handleRoleChange}
                    displayLabel={selectedRoleLabel}
                    placeholder="Search and select a role"
                    error={!!errors.primaryRole}
                  />
                  {errors.primaryRole && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.primaryRole}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Default role for login and reporting
                  </p>
                </div>

                <AdditionalRolesSelect
                  primaryRole={formData.primaryRole}
                  value={additionalRoles}
                  onChange={setAdditionalRoles}
                />

                {/* Phone */}
                <TextInput
                  variant="input"
                  type="tel"
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                />

                {/* Department */}
                <TextInput
                  variant="input"
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="Engineering, Sales, HR, etc."
                />
              </div>
            </div>

            {/* Note about password */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <svg className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-xs font-medium text-blue-900">Password Changes</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    To change the user's password, use the "Reset Password" feature separately.
                  </p>
                </div>
              </div>
            </div>

            {/* Status Toggle */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <div>
                    <label htmlFor="isActive" className="block text-sm font-semibold text-gray-900">
                      Active User
                    </label>
                    <p className="text-xs text-gray-500">User can log in and access the system</p>
                  </div>
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  formData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    formData.isActive ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            {/* Compact Preview */}
            {formData.email && (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-sm font-semibold text-indigo-900">Preview:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {formData.primaryRole && (
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-indigo-500"></div>
                        {selectedRoleLabel || formData.primaryRole}
                      </div>
                    )}
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      formData.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                        formData.isActive ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                      {formData.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="font-medium text-sm text-indigo-800">
                    {formData.firstName} {formData.lastName}
                  </div>
                  <div className="text-xs text-indigo-700 mt-1">{formData.email}</div>
                  {formData.department && (
                    <div className="text-xs text-indigo-600 mt-0.5">{formData.department}</div>
                  )}
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Update
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserEditForm;
